import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { MEMBER_ROLES, type MemberRole } from '@buildbee/contracts';
import { ROLE_LEVEL } from '@buildbee/domain';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Status,
  formatDate,
  formatRelative,
  useToast,
  type ColumnDef,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../lib/trpc';
import { CompanyHeader } from './nav';

type Team = RouterOutputs['platform']['members']['list'];
type Member = Team['members'][number];

/** Plain-language role names and what each can do (the role keys never change: Hive SSO maps to them). */
export const ROLE_INFO: Record<MemberRole, { label: string; description: string }> = {
  owner: { label: 'Owner', description: 'Everything, including billing and closing the company account.' },
  admin: { label: 'Admin', description: 'Runs the company: team, settings, every project and the money side.' },
  pm: { label: 'Project manager', description: 'Runs jobs: schedules, change orders, invoices and the client portal.' },
  sales: { label: 'Sales / estimator', description: 'Leads, deals, estimates and proposals.' },
  field: { label: 'Field', description: 'Daily logs, photos and punch lists on the projects they are added to.' },
  sub: { label: 'Subcontractor', description: 'Sees only the projects they are added to, and only their own work.' },
};

/** Highest role first, following the role ladder (owner > admin > pm · sales > field > sub). */
const byRank = (a: MemberRole, b: MemberRole) => ROLE_LEVEL[b] - ROLE_LEVEL[a];
const roleOptions = [...MEMBER_ROLES].sort(byRank).map((r) => ({ value: r, label: ROLE_INFO[r].label }));

type Pending =
  | { kind: 'role'; member: Member; role: MemberRole }
  | { kind: 'status'; member: Member; status: 'active' | 'suspended' }
  | { kind: 'remove'; member: Member }
  | { kind: 'revoke'; invitationId: string; email: string };

/** /admin/workspaces/$workspaceId/team: the company's people, run by BuildBee support. */
export function PlatformCompanyTeamPage({ workspaceId }: { workspaceId: string }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const query = trpc.platform.members.list.useQuery({ workspaceId });
  const [pending, setPending] = useState<Pending | null>(null);
  const [adding, setAdding] = useState(false);

  const done = (message: string) => {
    toast.success(message);
    setPending(null);
    void utils.platform.members.invalidate();
    void utils.platform.workspaces.invalidate();
    void utils.platform.audit.invalidate();
  };
  const onError = (err: unknown) => toast.error("That didn't work", errorMessage(err));
  const update = trpc.platform.members.update.useMutation({ onError });
  const remove = trpc.platform.members.remove.useMutation({ onSuccess: () => done('Removed from the company'), onError });
  const revoke = trpc.platform.members.revokeInvitation.useMutation({ onSuccess: () => done('Invitation revoked'), onError });

  if (query.isLoading) return <LoadingState />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const t = query.data;
  const activeOwners = t.members.filter((m) => m.role === 'owner' && m.status === 'active').length;
  const isLastOwner = (m: Member) => m.role === 'owner' && m.status === 'active' && activeOwners <= 1;

  const columns: ColumnDef<Member, unknown>[] = [
    {
      id: 'name',
      accessorFn: (m) => `${m.name} ${m.email}`,
      header: 'Person',
      cell: ({ row }) => (
        <div>
          <Link to="/admin/users/$userId" params={{ userId: row.original.userId }} className="font-medium hover:underline">
            {row.original.name}
          </Link>
          <div className="text-xs text-muted-foreground">{row.original.email}</div>
        </div>
      ),
    },
    {
      id: 'role',
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Select
          aria-label={`Role for ${row.original.name}`}
          value={row.original.role}
          options={roleOptions}
          disabled={isLastOwner(row.original)}
          title={isLastOwner(row.original) ? 'The only owner. Make someone else an owner first.' : undefined}
          onChange={(e) => setPending({ kind: 'role', member: row.original, role: e.target.value as MemberRole })}
        />
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) =>
        row.original.lockedAt ? (
          <Status tone="blocked">Account locked</Status>
        ) : row.original.status === 'suspended' ? (
          <Status tone="blocked">Suspended</Status>
        ) : (
          <Status tone="done">Active</Status>
        ),
    },
    {
      id: 'lastActiveAt',
      accessorKey: 'lastActiveAt',
      header: 'Last active',
      cell: ({ row }) => <span className="figures">{row.original.lastActiveAt ? formatRelative(row.original.lastActiveAt) : 'never'}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={isLastOwner(row.original)}
            onClick={() => setPending({ kind: 'status', member: row.original, status: row.original.status === 'suspended' ? 'active' : 'suspended' })}
          >
            {row.original.status === 'suspended' ? 'Reactivate' : 'Suspend'}
          </Button>
          <Button variant="ghost" size="sm" disabled={isLastOwner(row.original)} onClick={() => setPending({ kind: 'remove', member: row.original })}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  const confirm = (() => {
    if (!pending) return null;
    if (pending.kind === 'role') {
      return {
        title: `Make ${pending.member.name} ${ROLE_INFO[pending.role].label.toLowerCase()}?`,
        description: `${ROLE_INFO[pending.role].label}: ${ROLE_INFO[pending.role].description}`,
        label: 'Change role',
        destructive: pending.member.role === 'owner' || pending.member.role === 'admin',
        run: () => update.mutate({ workspaceId, membershipId: pending.member.id, role: pending.role }, { onSuccess: () => done('Role changed') }),
      };
    }
    if (pending.kind === 'status') {
      return pending.status === 'suspended'
        ? {
            title: `Suspend ${pending.member.name}?`,
            description: "They can't open this company until someone reactivates them. Their account and other companies are not affected.",
            label: 'Suspend',
            destructive: true,
            run: () => update.mutate({ workspaceId, membershipId: pending.member.id, status: 'suspended' }, { onSuccess: () => done('Suspended') }),
          }
        : {
            title: `Reactivate ${pending.member.name}?`,
            description: 'They can open this company again with their current role.',
            label: 'Reactivate',
            destructive: false,
            run: () => update.mutate({ workspaceId, membershipId: pending.member.id, status: 'active' }, { onSuccess: () => done('Reactivated') }),
          };
    }
    if (pending.kind === 'remove') {
      return {
        title: `Remove ${pending.member.name} from ${t.workspace.name}?`,
        description: 'They lose access to this company straight away. Their BuildBee account, and anything they created here, stays.',
        label: 'Remove',
        destructive: true,
        run: () => remove.mutate({ workspaceId, membershipId: pending.member.id }),
      };
    }
    return {
      title: `Revoke the invitation to ${pending.email}?`,
      description: 'The link in their email stops working.',
      label: 'Revoke',
      destructive: true,
      run: () => revoke.mutate({ workspaceId, invitationId: pending.invitationId }),
    };
  })();

  return (
    <div className="space-y-6">
      <CompanyHeader workspaceId={workspaceId} current="team" name={t.workspace.name} />
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>Team</CardTitle>
            <CardDescription>Changes here show in the company&apos;s own audit log as “BuildBee support”.</CardDescription>
          </div>
          <Button onClick={() => setAdding(true)}>Add person</Button>
        </CardHeader>
        <CardContent>
          {t.workspace.billingMode === 'hive' && (
            <Alert variant="info" className="mb-4">
              Roles for this company are managed in Hive. You can still change them here, but an owner or admin role set in Hive is applied
              again the next time the person signs in from Hive, and Hive can suspend or remove people.
            </Alert>
          )}
          <DataTable columns={columns} data={[...t.members].sort((a, b) => byRank(a.role as MemberRole, b.role as MemberRole) || a.name.localeCompare(b.name))} getRowId={(m) => m.id} searchPlaceholder="Search people" empty={{ title: 'No one on this team yet' }} />
        </CardContent>
      </Card>

      {t.invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Invited</CardTitle>
            <CardDescription>People who haven&apos;t accepted yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border text-sm">
              {t.invitations.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    {i.email} <span className="text-muted-foreground">· {ROLE_INFO[i.role as MemberRole]?.label ?? i.role}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {i.expired ? <Status tone="risk">Expired</Status> : <span className="figures text-muted-foreground">expires {formatDate(i.expiresAt)}</span>}
                    <Button variant="ghost" size="sm" onClick={() => setPending({ kind: 'revoke', invitationId: i.id, email: i.email })}>
                      Revoke
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <AddPersonDialog workspaceId={workspaceId} companyName={t.workspace.name} open={adding} onOpenChange={setAdding} />

      {confirm && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPending(null)}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.label}
          destructive={confirm.destructive}
          loading={update.isPending || remove.isPending || revoke.isPending}
          onConfirm={confirm.run}
        />
      )}
    </div>
  );
}

function AddPersonDialog({ workspaceId, companyName, open, onOpenChange }: { workspaceId: string; companyName: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('pm');
  const [title, setTitle] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const add = trpc.platform.members.add.useMutation({
    onSuccess: (out) => {
      void utils.platform.members.invalidate();
      void utils.platform.audit.invalidate();
      toast.success(out.kind === 'added' ? `Added to ${companyName}` : `Invitation sent to ${email}`);
      if (out.inviteUrl) setInviteUrl(out.inviteUrl);
      else close();
    },
    onError: (err) => toast.error("Couldn't add them", errorMessage(err)),
  });
  const close = () => {
    setEmail('');
    setTitle('');
    setRole('pm');
    setInviteUrl(null);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        title={`Add someone to ${companyName}`}
        description="If they already have a BuildBee account they're added straight away. A new email address gets an invitation."
      >
        {inviteUrl ? (
          <div className="space-y-3">
            <Alert variant="info" title="Invitation link (test environments only)">
              <span className="figures break-all">{inviteUrl}</span>
            </Alert>
            <Button onClick={close}>Done</Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate({ workspaceId, email: email.trim(), role, title: title.trim() || undefined });
            }}
          >
            <Field label="Email" htmlFor="ap-email" required>
              <Input id="ap-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Role" htmlFor="ap-role" description={ROLE_INFO[role].description}>
              <Select id="ap-role" value={role} options={roleOptions} onChange={(e) => setRole(e.target.value as MemberRole)} />
            </Field>
            <Field label="Job title" htmlFor="ap-title">
              <Input id="ap-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional" />
            </Field>
            <p className="text-sm text-muted-foreground">Clients are invited from the company&apos;s Client portal, not here.</p>
            <Button type="submit" disabled={!email.includes('@')} loading={add.isPending}>
              Add person
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
